/**
 * Zustand Social Store (AC-856)
 *
 * Manages social/sharing state including:
 * - Explore projects browsing
 * - Following feed
 * - User profile viewing
 * - Follow/unfollow actions
 * - User search
 */

import { create } from 'zustand';
import apiService from '../services/api';
import {
  SharedProjectCard,
  UserPublicProfile,
  UserPublicSummary,
} from '../types';

interface SocialState {
  // Explore
  exploreProjects: SharedProjectCard[];
  exploreTotalCount: number;
  exploreLoading: boolean;
  loadExploreProjects: (sort: string, skip: number, limit: number, query?: string) => Promise<void>;

  // Following feed
  feedProjects: SharedProjectCard[];
  feedTotalCount: number;
  feedLoading: boolean;
  loadFollowingFeed: (skip: number, limit: number) => Promise<void>;

  // User profile
  viewedProfile: UserPublicProfile | null;
  profileLoading: boolean;
  loadUserProfile: (userId: number) => Promise<void>;

  // Follow actions
  followUser: (userId: number) => Promise<void>;
  unfollowUser: (userId: number) => Promise<void>;

  // Search
  searchResults: UserPublicSummary[];
  searchLoading: boolean;
  searchUsers: (query: string) => Promise<void>;
}

export const useSocialStore = create<SocialState>((set, get) => ({
  // Explore state
  exploreProjects: [],
  exploreTotalCount: 0,
  exploreLoading: false,

  loadExploreProjects: async (sort: string, skip: number, limit: number, query?: string) => {
    set({ exploreLoading: true });
    try {
      const response = await apiService.getExploreProjects(sort, skip, limit, undefined, query);
      set({
        exploreProjects: response.projects,
        exploreTotalCount: response.total,
        exploreLoading: false,
      });
    } catch (error) {
      set({ exploreLoading: false });
      console.error('Failed to load explore projects:', error);
    }
  },

  // Following feed state
  feedProjects: [],
  feedTotalCount: 0,
  feedLoading: false,

  loadFollowingFeed: async (skip: number, limit: number) => {
    set({ feedLoading: true });
    try {
      const response = await apiService.getFollowingFeed(skip, limit);
      set({
        feedProjects: response.projects,
        feedTotalCount: response.total,
        feedLoading: false,
      });
    } catch (error) {
      set({ feedLoading: false });
      console.error('Failed to load following feed:', error);
    }
  },

  // User profile state
  viewedProfile: null,
  profileLoading: false,

  loadUserProfile: async (userId: number) => {
    set({ profileLoading: true });
    try {
      const profile = await apiService.getUserProfile(userId);
      set({ viewedProfile: profile, profileLoading: false });
    } catch (error) {
      set({ profileLoading: false });
      console.error('Failed to load user profile:', error);
    }
  },

  // Follow actions
  followUser: async (userId: number) => {
    try {
      await apiService.followUser(userId);
      // Update viewed profile if it matches the followed user
      const { viewedProfile } = get();
      if (viewedProfile && viewedProfile.user_id === userId) {
        set({
          viewedProfile: {
            ...viewedProfile,
            is_following: true,
            follower_count: viewedProfile.follower_count + 1,
          },
        });
      }
    } catch (error) {
      console.error('Failed to follow user:', error);
    }
  },

  unfollowUser: async (userId: number) => {
    try {
      await apiService.unfollowUser(userId);
      // Update viewed profile if it matches the unfollowed user
      const { viewedProfile } = get();
      if (viewedProfile && viewedProfile.user_id === userId) {
        set({
          viewedProfile: {
            ...viewedProfile,
            is_following: false,
            follower_count: Math.max(0, viewedProfile.follower_count - 1),
          },
        });
      }
    } catch (error) {
      console.error('Failed to unfollow user:', error);
    }
  },

  // Search state
  searchResults: [],
  searchLoading: false,

  searchUsers: async (query: string) => {
    set({ searchLoading: true });
    try {
      const response = await apiService.searchUsers(query, 0, 20);
      set({ searchResults: response.users, searchLoading: false });
    } catch (error) {
      set({ searchLoading: false });
      console.error('Failed to search users:', error);
    }
  },
}));

export default useSocialStore;
